import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Fingerprint as FingerprintIcon,
  Folder as FolderIcon,
  Share as ShareIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const EIDSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Enforcement Integrated Database (EID)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            DHS Central Enforcement Case Management and Biometric System
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Enforcement Integrated Database (EID) is a comprehensive, centralized database system that supports 
            enforcement case tracking, biometric matching, and cross-agency data sharing across Department of Homeland 
            Security (DHS) components. EID serves as the mission-critical backbone for immigration enforcement operations, 
            providing real-time access to case information, biometric verification, and investigative intelligence.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary - Compact Table Format */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="EID System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is EID"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A centralized <strong>enforcement case management system</strong> integrating biometric identification, 
                    case tracking, and investigative intelligence across DHS components. EID consolidates data from multiple 
                    legacy systems to provide a <strong>single source of truth</strong> for enforcement operations, supporting 
                    ICE, CBP, and other DHS agencies in immigration enforcement activities.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses EID"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>DHS Immigration and Customs Enforcement (ICE)</strong> with access provided to 
                    U.S. Customs and Border Protection (CBP), U.S. Citizenship and Immigration Services (USCIS), and other 
                    federal, state, and local law enforcement agencies. Primary users include <strong>enforcement officers, 
                    investigators, case managers, and intelligence analysts</strong> across the immigration enforcement community.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was EID deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed in <strong>2010</strong> as part of ICE's modernization initiative to replace legacy enforcement 
                    systems. Continuously enhanced with <strong>biometric integration (2012)</strong>, mobile access capabilities 
                    (2015), and advanced analytics (2018-present). Operates <strong>24/7/365</strong> supporting real-time 
                    enforcement operations nationwide.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is EID deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed across <strong>all ICE field offices, detention facilities, and enforcement operations centers</strong> 
                    nationwide. Accessible at CBP ports of entry, Border Patrol stations, and USCIS field offices. Cloud-based 
                    architecture enables <strong>mobile access for field agents</strong> and integration with state/local law 
                    enforcement through <strong>Law Enforcement Support Center (LESC)</strong> and Secure Communities program.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why EID exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>unified enforcement case management</strong> and eliminate data silos across DHS components. 
                    Enables accurate biometric identification, tracks enforcement actions from apprehension through removal, 
                    supports investigative intelligence sharing, and ensures <strong>officer safety</strong> through real-time 
                    access to subject history and threat information. Critical for <strong>national security and public safety</strong> 
                    mission execution.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core System Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FolderIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Case Management & Tracking
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive lifecycle management of enforcement cases from initial encounter through final disposition.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Case Creation:</strong> Automated case initiation from encounters and arrests</Typography></li>
                <li><Typography variant="body2"><strong>Workflow Management:</strong> Tracks cases through investigation, detention, court proceedings</Typography></li>
                <li><Typography variant="body2"><strong>Document Management:</strong> Digital storage of warrants, orders, evidence</Typography></li>
                <li><Typography variant="body2"><strong>Status Tracking:</strong> Real-time case status updates and notifications</Typography></li>
                <li><Typography variant="body2"><strong>Removal Operations:</strong> Coordinates deportation and repatriation activities</Typography></li>
                <li><Typography variant="body2"><strong>Audit Trail:</strong> Complete history of case actions and decisions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FingerprintIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Biometric Matching & Verification
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced biometric identification and verification capabilities for accurate subject identification.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Fingerprint Matching:</strong> 10-print and latent fingerprint comparison</Typography></li>
                <li><Typography variant="body2"><strong>Facial Recognition:</strong> Photo matching against DHS databases</Typography></li>
                <li><Typography variant="body2"><strong>Iris Scanning:</strong> Biometric iris pattern verification</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Modal Fusion:</strong> Combined biometric matching for accuracy</Typography></li>
                <li><Typography variant="body2"><strong>IDENT Integration:</strong> Real-time queries to DHS biometric repository</Typography></li>
                <li><Typography variant="body2"><strong>FBI IAFIS Interface:</strong> Criminal history and fingerprint checks</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShareIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Cross-Agency Data Sharing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Secure information exchange across federal, state, and local law enforcement agencies.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>DHS Integration:</strong> Real-time data sharing with ICE, CBP, USCIS, TSA</Typography></li>
                <li><Typography variant="body2"><strong>NCIC Interface:</strong> FBI National Crime Information Center connectivity</Typography></li>
                <li><Typography variant="body2"><strong>State/Local Access:</strong> Secure Communities and 287(g) program support</Typography></li>
                <li><Typography variant="body2"><strong>International Sharing:</strong> INTERPOL and foreign law enforcement coordination</Typography></li>
                <li><Typography variant="body2"><strong>LESC Support:</strong> Law Enforcement Support Center query responses</Typography></li>
                <li><Typography variant="body2"><strong>Privacy Controls:</strong> Role-based access and audit logging</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SearchIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Investigation Support Tools
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced analytical and investigative capabilities for enforcement operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Subject Profiles:</strong> Comprehensive individual history and associations</Typography></li>
                <li><Typography variant="body2"><strong>Link Analysis:</strong> Relationship mapping and network visualization</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Integration:</strong> Fusion with classified and open-source intel</Typography></li>
                <li><Typography variant="body2"><strong>Threat Assessment:</strong> Risk scoring and priority classification</Typography></li>
                <li><Typography variant="body2"><strong>Geospatial Analysis:</strong> Location-based pattern identification</Typography></li>
                <li><Typography variant="body2"><strong>Predictive Analytics:</strong> ML-based trend forecasting and targeting</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Architecture */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Data Architecture & Integration
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data Category</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Source Systems</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data Elements</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Biographic Data</strong></TableCell>
              <TableCell>TECS, IDENT, CIS, EARM</TableCell>
              <TableCell>Name, DOB, nationality, aliases, addresses, family relationships</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Biometric Data</strong></TableCell>
              <TableCell>IDENT, FBI IAFIS, State ID systems</TableCell>
              <TableCell>Fingerprints, facial images, iris scans, DNA profiles</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Enforcement History</strong></TableCell>
              <TableCell>Legacy ICE systems, EARM, ENFORCE</TableCell>
              <TableCell>Arrests, detentions, removals, bond history, court proceedings</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Immigration Records</strong></TableCell>
              <TableCell>USCIS CIS, CLAIMS, ELIS</TableCell>
              <TableCell>Visa applications, status adjustments, naturalization, petitions</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Criminal History</strong></TableCell>
              <TableCell>FBI NCIC, State repositories</TableCell>
              <TableCell>Arrests, convictions, warrants, gang affiliations, sex offender status</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Travel Records</strong></TableCell>
              <TableCell>TECS, ADIS, Carrier systems</TableCell>
              <TableCell>Border crossings, flight manifests, visa issuance, entry/exit records</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Intelligence Data</strong></TableCell>
              <TableCell>ICE HSI, FBI, JTTF, Fusion Centers</TableCell>
              <TableCell>Threat assessments, investigative leads, classified intelligence</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Detention Records</strong></TableCell>
              <TableCell>ENFORCE, Facility management systems</TableCell>
              <TableCell>Custody status, facility location, medical records, visitation logs</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Statistics */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                15M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subject Records
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Unique individuals tracked
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                50M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Biometric Records
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Fingerprints and photos
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                25K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Across DHS and partners
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                2M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Queries
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Daily average: 5,500
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Mission-Critical Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Unified Data Repository:</strong> Single source of truth eliminating data silos</Typography></li>
                <li><Typography variant="body2"><strong>Officer Safety:</strong> Real-time access to subject history and threat information</Typography></li>
                <li><Typography variant="body2"><strong>Accurate Identification:</strong> Multi-modal biometric verification reduces identity fraud</Typography></li>
                <li><Typography variant="body2"><strong>Operational Efficiency:</strong> Streamlined workflows and automated processes</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Intelligence Fusion:</strong> Integrated view of enforcement and intelligence data</Typography></li>
                <li><Typography variant="body2"><strong>Interagency Collaboration:</strong> Seamless information sharing across jurisdictions</Typography></li>
                <li><Typography variant="body2"><strong>Case Continuity:</strong> Complete lifecycle tracking from apprehension to removal</Typography></li>
                <li><Typography variant="body2"><strong>Compliance & Audit:</strong> Comprehensive logging for oversight and accountability</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Security & Privacy */}
      <Card sx={{ mb: 4, bgcolor: '#fff3e0', border: '2px solid #F57C00' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
            Security & Privacy Safeguards
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Access Controls:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Role-based access control (RBAC)</Typography></li>
                <li><Typography variant="body2">Multi-factor authentication</Typography></li>
                <li><Typography variant="body2">Need-to-know enforcement</Typography></li>
                <li><Typography variant="body2">Compartmented data access</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Privacy Compliance:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Privacy Impact Assessments (PIAs)</Typography></li>
                <li><Typography variant="body2">System of Records Notices (SORNs)</Typography></li>
                <li><Typography variant="body2">Data retention policies</Typography></li>
                <li><Typography variant="body2">Privacy Act compliance</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Audit & Oversight:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Comprehensive audit logging</Typography></li>
                <li><Typography variant="body2">User activity monitoring</Typography></li>
                <li><Typography variant="body2">DHS OIG oversight</Typography></li>
                <li><Typography variant="body2">Congressional reporting</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Ecosystem */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Integration with DHS Enforcement Ecosystem
          </Typography>
          <Typography variant="body2" paragraph>
            EID serves as the central hub for enforcement operations, integrating with dozens of DHS and external systems:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="TECS (Border Database)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Primary enforcement database</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="IDENT (Biometric System)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">DHS biometric repository</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="ENFORCE (Detention)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Detention management</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="EARM (Alien Removal)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Removal operations</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="FBI NCIC/IAFIS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Criminal history checks</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="USCIS CIS/ELIS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Immigration benefits data</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="LESC (Support Center)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Law enforcement queries</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="State/Local Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">287(g) and partnerships</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EIDSystemDetail;
